import ArrowLeft from '@assets/icons/arrow-left.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { ACCOUNT_ORDERS_FIELDS_FRAGMENT } from '@/components/locations/account-fragments';
import { AccountForm } from '@/components/locations/account-form';
import { ADDRESS_FIELDS_FRAGMENT } from '@/components/addresses/address-input';
import { AddressInputType } from '@domain/addresses/types/types';
import { gql, useMutation } from '@apollo/client';
import { LOCATION_MANAGER_ROUTES } from '@/navigators/routes';
import { LocationManagerStackParams } from '@/navigators/types';
import {
  Locations_EditRestaurantMutation,
  Locations_EditRestaurantMutationVariables,
} from '@/types';
import { ModalContainer } from '@/components/modal-container';
import { StackScreenProps } from '@react-navigation/stack';
import { Type } from '@/components/toast';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useToasts } from '@/contexts/toast-context';

export const ACCOUNT_FIELDS_FRAGMENT = gql`
  fragment Locations_AccountRelationshipFields on chats {
    ...AccountOrdersFields
    chat_route_metadata {
      name
    }
    restaurant_account_id
    restaurant_notes
    uuid
  }

  fragment Locations_AccountFields on businesses {
    address {
      ...AddressInputFields
    }
    name
    restaurant_chats(
      where: { supplier_uuid: { _eq: $supplierUUID } }
      limit: 1
    ) {
      ...Locations_AccountRelationshipFields
    }
    uuid
  }

  ${ACCOUNT_ORDERS_FIELDS_FRAGMENT}
  ${ADDRESS_FIELDS_FRAGMENT}
`;

export const EDIT_RESTAURANT_MUTATION = gql`
  mutation Locations_EditRestaurant(
    $supplierUUID: uuid!
    $restaurantUUID: uuid!
    $name: String
    $address: String
    $address2: String
    $accountID: String
    $notes: String
    $routeStop: String
    $unplacedOrderStatuses: [String!]!
  ) {
    supplierUpdateRestaurant(
      restaurant_account_id: $accountID
      restaurant_address: $address
      restaurant_address2: $address2
      restaurant_id: $restaurantUUID
      restaurant_name: $name
      restaurant_notes: $notes
      route_stop: $routeStop
      supplier_id: $supplierUUID
    ) {
      business {
        ...Locations_AccountFields
      }
    }
  }

  ${ACCOUNT_FIELDS_FRAGMENT}
`;

type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.EditAccount
>;

export const EditAccountScreen: React.FC<Props> = ({ navigation, route }) => {
  const {
    accountID,
    onClose,
    restaurantAddress,
    restaurantName,
    restaurantNotes,
    restaurantRouteStop,
    restaurantUUID,
    supplierUUID,
  } = route.params;
  const { showToast, showErrorToast } = useToasts();

  const [name, setName] = useState(restaurantName);
  const [address, setAddress] = useState<AddressInputType | null>(
    restaurantAddress,
  );
  const [restaurantAccountID, setRestaurantAccountID] = useState(accountID);
  const [notes, setNotes] = useState(restaurantNotes);
  const [routeStop, setRouteStop] = useState(restaurantRouteStop || '');
  const [address2, setAddress2] = useState(restaurantAddress?.address2 || '');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    // Listen to beforeRemove since the user can close modals via the overlay
    return navigation.addListener('beforeRemove', () => {
      onClose?.();
    });
  }, [navigation, onClose]);

  const addressInputAddress2 = address?.address2;
  useEffect(() => {
    if (addressInputAddress2) {
      setAddress2(addressInputAddress2);
    }
  }, [addressInputAddress2]);

  function createChangeCallback<T>(
    setValue: React.Dispatch<React.SetStateAction<T>>,
  ) {
    return (value: T) => {
      setIsDirty(true);
      setValue(value);
    };
  }

  const [updateRestaurant, { loading: isUpdatingRestaurant }] = useMutation<
    Locations_EditRestaurantMutation,
    Locations_EditRestaurantMutationVariables
  >(EDIT_RESTAURANT_MUTATION);

  return (
    <ModalContainer
      navigationHeaderProps={{
        BackButtonIcon: ArrowLeft,
        showBackButton: true,
        showShadow: true,
        title: restaurantName,
      }}
      showLargeLayoutCloseButton={false}
    >
      <AccountForm
        accountID={restaurantAccountID}
        address={address}
        address2={address2}
        buttonProps={{
          disabled: !address || !name || !isDirty,
          loading: isUpdatingRestaurant,
          onPress: async () => {
            try {
              const { data: updatedData } = await updateRestaurant({
                variables: {
                  accountID: restaurantAccountID || null,
                  address: address!.address,
                  address2: address2 || null,
                  name: name!,
                  notes: notes || null,
                  restaurantUUID,
                  routeStop: routeStop || null,
                  supplierUUID: supplierUUID,
                  unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
                },
              });
              setIsDirty(false);

              // update params to update title
              navigation.setParams({
                restaurantName:
                  updatedData?.supplierUpdateRestaurant.business?.name ||
                  restaurantName,
              });

              showToast({
                content: i18n.t('edit_account.success'),
                type: Type.POSITIVE,
              });
            } catch (e) {
              showErrorToast();
            }
          },
          trackingData: {
            name: 'edit_account.submit_button',
            properties: {
              restaurantUUID,
            },
          },
        }}
        buttonText={i18n.t('save')}
        name={name}
        notes={notes}
        onAccountIDChange={createChangeCallback(setRestaurantAccountID)}
        onAddress2Change={createChangeCallback(setAddress2)}
        onAddressChange={createChangeCallback(setAddress)}
        onNameChange={createChangeCallback(setName)}
        onNotesChange={createChangeCallback(setNotes)}
        onRouteStopChange={createChangeCallback(setRouteStop)}
        routeStop={routeStop}
      />
    </ModalContainer>
  );
};
